import React, { useState, useRef, useEffect } from "react";
import { Tooltip } from "antd";
import { PlusOutlined, CloseOutlined, FileOutlined } from "@ant-design/icons";
import Logger from "@/utility/logger";

interface Tab {
  id: string;
  title: string;
  description: string;
  content?: React.ReactNode;
  closable?: boolean;
  icon?: React.ReactNode;
}

interface TabsProps {
  tabs: Tab[];
  setTabs: React.Dispatch<React.SetStateAction<Tab[]>>;
  activeTabId: string;
  setActiveTabId: React.Dispatch<React.SetStateAction<string>>;
  showNewTabButton?: boolean;
}

function Tabs({ tabs, setTabs, activeTabId, setActiveTabId, showNewTabButton = true }: TabsProps) {
  const maxTabWidth = 240;
  const [draggedTab, setDraggedTab] = useState<string | null>(null);
  const [dragOverTab, setDragOverTab] = useState<string | null>(null);
  const [tabWidth, setTabWidth] = useState<number>(maxTabWidth);
  const [nextTabId, setNextTabId] = useState(4);
  const tabsRef = useRef<HTMLDivElement>(null);

  // Update active tab if tabs change
  useEffect(() => {
    if (!tabs.find((tab) => tab.id === activeTabId) && tabs.length > 0) {
      setActiveTabId(tabs[0].id);
    }
  }, [tabs, activeTabId]);

  // Calculate tab width based on number of tabs
  const calculateTabWidth = () => {
    if (!tabsRef.current) return setTabWidth(maxTabWidth);

    const containerWidth = tabsRef.current.offsetWidth - (showNewTabButton ? 40 : 0);
    const tabWidth = Math.max(32, Math.min(maxTabWidth, containerWidth / tabs.length));
    setTabWidth(tabWidth);
  };

  /* Managing dynamic tabs handlers */
  const handleNewTab = () => {
    const newTab: Tab = {
      id: `tab-${nextTabId}`,
      title: `New Tab ${nextTabId}`,
      description: `Dynamically created tab`,
      icon: <FileOutlined />,
      content: (
        <div className="p-8">
          <h1 className="text-3xl font-bold mb-4">New Tab {nextTabId}</h1>
          <p className="text-gray-600 mb-4">This is a dynamically created tab. You can add any content here.</p>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-700">
              Tab ID: <code className="bg-gray-200 px-2 py-1 rounded">tab-{nextTabId}</code>
            </p>
            <p className="text-sm text-gray-700 mt-2">Created at: {new Date().toLocaleTimeString()}</p>
          </div>
        </div>
      ),
    };

    setTabs((prevTabs) => [...prevTabs, newTab]);
    setNextTabId((prev) => prev + 1);
  };

  const handleTabClick = (tabId: string) => {
    setActiveTabId(tabId);
    Logger.trace("Tabs.tsx: Tab changed to:", tabId);
  };

  const handleTabClose = (e: React.MouseEvent, tabId: string) => {
    e.stopPropagation();
    setTabs((prevTabs) => prevTabs.filter((tab) => tab.id !== tabId));
    Logger.trace("Tabs.tsx: removed tab: ", tabId);

    // If closing active tab, switch to another tab
    if (tabId === activeTabId && tabs.length > 1) {
      const currentIndex = tabs.findIndex((tab) => tab.id === tabId);
      const nextTab = tabs[currentIndex + 1] || tabs[currentIndex - 1];
      if (nextTab) {
        setActiveTabId(nextTab.id);
        Logger.trace("Tabs.tsx: Tab changed to:", tabId);
      }
    }
  };

  /* Drag and drop handlers */
  const handleDragStart = (e: React.DragEvent, tabId: string) => {
    setDraggedTab(tabId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, tabId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverTab(tabId);
  };

  const handleDragLeave = () => {
    setDragOverTab(null);
  };

  const handleDrop = (e: React.DragEvent, targetTabId: string) => {
    e.preventDefault();
    setDragOverTab(null);

    if (draggedTab && draggedTab !== targetTabId) {
      Logger.debug(`Tabs.tsx: Move tab ${draggedTab} to position of ${targetTabId}`);
      setTabs((prevTabs) => {
        const draggedIndex = prevTabs.findIndex((tab) => tab.id === draggedTab);
        const targetIndex = prevTabs.findIndex((tab) => tab.id === targetTabId);
        Logger.debug("Tabs.tsx: dragged: ", draggedTab, " from ", draggedIndex, " to target: ", targetIndex);

        // If either is not found, return previous state unchanged
        if (draggedIndex === -1 || targetIndex === -1) {
          Logger.debug("Tabs.tsx: tab not found");
          return prevTabs;
        }

        const updatedTabs = [...prevTabs];
        const [draggedTabContent] = updatedTabs.splice(draggedIndex, 1); // remove dragged tab

        // insert dragged tab before targetIndex (if draggedIndex < targetIndex, targetIndex changes after removal)
        const newTargetIndex = draggedIndex < targetIndex ? targetIndex - 1 : targetIndex;
        updatedTabs.splice(newTargetIndex, 0, draggedTabContent);

        return updatedTabs;
      });
    }

    setDraggedTab(null);
    Logger.debug("Tabs.tsx: Dragged tab set to null");
  };

  const handleDragEnd = () => {
    setDraggedTab(null);
    setDragOverTab(null);
    Logger.debug("Tabs.tsx: Dragged tab set to null");
  };

  return (
    <div className={`chrome-tabs-bar active-tab-${activeTabId}`} ref={tabsRef}>
      <div className="chrome-tabs-list">
        {tabs.map((tab, index) => {
          const isActive = tab.id === activeTabId;
          const isDragged = draggedTab === tab.id;
          const isDragOver = dragOverTab === tab.id;

          return (
            <Tooltip
              key={tab.id}
              placement="bottom"
              title={`${tab.description !== "" ? tab.description : tab.description}`}
            >
              <div
                className={`chrome-tab chrome-tab-${tab.id} ${isActive ? "active" : "inactive"} ${
                  isDragged ? "dragged" : ""
                } ${isDragOver ? "drag-over" : ""}`}
                style={{
                  width: `${tabWidth}px`,
                  zIndex: isActive ? 10 : 5 - index,
                }}
                onClick={() => handleTabClick(tab.id)}
                draggable
                onDragStart={(e) => handleDragStart(e, tab.id)}
                onDragOver={(e) => handleDragOver(e, tab.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, tab.id)}
                onDragEnd={handleDragEnd}
              >
                {/* Tab Background */}
                <div className="chrome-tab-background" />

                {/* Tab Content */}
                <div className="chrome-tab-content">
                  {tab.icon && <div className="chrome-tab-icon">{tab.icon}</div>}

                  <div className="chrome-tab-title">{tab.title}</div>

                  {tab.closable !== false && tabs.length > 1 && (
                    <button
                      className="chrome-tab-close"
                      onClick={(e) => handleTabClose(e, tab.id)}
                      aria-label={`Close ${tab.title}`}
                    >
                      <CloseOutlined style={{ fontSize: "0.5rem" }} />
                    </button>
                  )}
                </div>
              </div>
            </Tooltip>
          );
        })}

        {/* New Tab Button */}
        {showNewTabButton && (
          <button className="chrome-new-tab-button" onClick={handleNewTab}>
            <PlusOutlined style={{ fontSize: "1rem" }} />
          </button>
        )}
      </div>

      {/* Tab Bar Bottom Border */}
      <div className="chrome-tabs-border" />
    </div>
  );
}

export default Tabs;
